/**
 * Asset-free procedural audio via the Web Audio API. We ship no sound files;
 * everything here is synthesized — short SFX for actions, a very low ambience
 * layer that shifts day↔night, a fire crackle when near a campfire, a looping
 * retro chiptune score, and proximity beds/cues for nearby players and creatures.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private enabled = true;

  // ambient
  private droneOsc?: OscillatorNode;
  private droneGain?: GainNode;
  private noiseSrc?: AudioBufferSourceNode;
  private noiseGain?: GainNode;
  private noiseFilter?: BiquadFilterNode;
  private noiseFilter2?: BiquadFilterNode;

  // fire
  private fireGain?: GainNode;

  // background music
  private musicGain?: GainNode;
  private musicTimer?: number;
  private darknessLevel = 0;
  private bloodMoon = false;
  private musicStep = 0;

  // proximity beds (silent until entities are near)
  private presenceGain?: GainNode; // warm murmur — players / friendly NPCs near
  private menaceGain?: GainNode; // low tremor — hostile creatures near

  private noiseBuffer?: AudioBuffer;

  /** Must be called from a user gesture (click/keydown) to unlock audio. */
  resume() {
    if (!this.ctx) this.init();
    if (this.ctx && this.ctx.state === "suspended") void this.ctx.resume();
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    if (this.master) this.master.gain.value = this.enabled ? 0.9 : 0;
    return this.enabled;
  }
  get isEnabled() {
    return this.enabled;
  }

  private init() {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.enabled ? 0.9 : 0;
    this.master.connect(this.ctx.destination);

    // white-noise buffer reused for wind / fire / hits
    const len = this.ctx.sampleRate * 2;
    this.noiseBuffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;

    this.startAmbient();
    this.startMusic();
  }

  private startAmbient() {
    if (!this.ctx) return;
    // low ambience. Keep this deliberately subtle: the old value read as a
    // constant hum under headphones, so the score now carries the musical bed.
    this.droneOsc = this.ctx.createOscillator();
    this.droneOsc.type = "sine";
    this.droneOsc.frequency.value = 82;
    this.droneGain = this.ctx.createGain();
    this.droneGain.gain.value = 0.012;
    this.droneOsc.connect(this.droneGain).connect(this.master);
    this.droneOsc.start();

    // filtered wind noise — two cascaded lowpasses (24 dB/oct) keep it dark
    // and breathy so it reads as wind, not rain/static
    this.noiseSrc = this.ctx.createBufferSource();
    this.noiseSrc.buffer = this.noiseBuffer!;
    this.noiseSrc.loop = true;
    this.noiseFilter = this.ctx.createBiquadFilter();
    this.noiseFilter.type = "lowpass";
    this.noiseFilter.frequency.value = 240;
    this.noiseFilter2 = this.ctx.createBiquadFilter();
    this.noiseFilter2.type = "lowpass";
    this.noiseFilter2.frequency.value = 240;
    this.noiseGain = this.ctx.createGain();
    this.noiseGain.gain.value = 0.01;
    this.noiseSrc
      .connect(this.noiseFilter)
      .connect(this.noiseFilter2)
      .connect(this.noiseGain)
      .connect(this.master);
    this.noiseSrc.start();

    // fire bed (silent until near a fire) — two noise bands layered:
    // a low wood-body crackle and a mid snap, so it reads as fire not hiss
    this.fireGain = this.ctx.createGain();
    this.fireGain.gain.value = 0;
    const fireLayers: Array<[number, number, number]> = [
      [280, 1.4, 0.55], // low crackle / wood body
      [700, 0.9, 0.45], // mid snap
    ];
    for (const [freq, q, weight] of fireLayers) {
      const fireSrc = this.ctx.createBufferSource();
      fireSrc.buffer = this.noiseBuffer!;
      fireSrc.loop = true;
      const fireFilter = this.ctx.createBiquadFilter();
      fireFilter.type = "bandpass";
      fireFilter.frequency.value = freq;
      fireFilter.Q.value = q;
      const layerGain = this.ctx.createGain();
      layerGain.gain.value = weight;
      fireSrc.connect(fireFilter).connect(layerGain).connect(this.fireGain);
      fireSrc.start();
    }
    this.fireGain.connect(this.master);
  }

  /** darkness 0..1 — deepens the ambience at night, and shifts the music. */
  setNight(darkness: number) {
    this.darknessLevel = darkness;
    if (!this.ctx || !this.droneOsc || !this.noiseFilter || !this.noiseFilter2 || !this.noiseGain)
      return;
    const t = this.ctx.currentTime;
    this.droneOsc.frequency.linearRampToValueAtTime(82 - darkness * 18, t + 1);
    this.noiseFilter.frequency.linearRampToValueAtTime(240 + darkness * 140, t + 1);
    this.noiseFilter2.frequency.linearRampToValueAtTime(240 + darkness * 140, t + 1);
    this.noiseGain.gain.linearRampToValueAtTime(0.01 + darkness * 0.006, t + 1);
  }

  /** Blood Moon switches to a faster, tenser arrangement without restarting audio. */
  setBloodMoon(active: boolean) {
    if (this.bloodMoon === active) return;
    this.bloodMoon = active;
    this.musicStep = 0;
    this.restartMusicTimer();
  }

  /** nearFire 0/1 — fades the crackle bed in/out. */
  setNearFire(near: boolean) {
    if (!this.ctx || !this.fireGain) return;
    this.fireGain.gain.linearRampToValueAtTime(near ? 0.055 : 0, this.ctx.currentTime + 0.4);
  }

  // ---- background music ------------------------------------------------
  /**
   * Generative, asset-free score: original 8-bit-style patterns inspired by
   * early home-computer platformers. Short square/triangle notes form a
   * hypnotic loop; Blood Moon swaps to the same motif at a faster tempo with a
   * darker bass line. Two silent proximity beds are also wired here.
   */
  private startMusic() {
    if (!this.ctx) return;
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.38;

    // soft exponential-decay reverb on the music bus for warmth and space
    const irLen = Math.floor(this.ctx.sampleRate * 1.2);
    const impulse = this.ctx.createBuffer(1, irLen, this.ctx.sampleRate);
    const irData = impulse.getChannelData(0);
    for (let i = 0; i < irLen; i++)
      irData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / irLen, 4);
    const convolver = this.ctx.createConvolver();
    convolver.normalize = true;
    convolver.buffer = impulse;
    const dryGain = this.ctx.createGain();
    dryGain.gain.value = 1.0;
    const wetGain = this.ctx.createGain();
    wetGain.gain.value = 0.22;
    this.musicGain.connect(dryGain).connect(this.master);
    this.musicGain.connect(convolver).connect(wetGain).connect(this.master);

    this.restartMusicTimer();

    // presence bed — warm sine that fades in when players/friendly NPCs are near
    this.presenceGain = this.ctx.createGain();
    this.presenceGain.gain.value = 0;
    const pres = this.ctx.createOscillator();
    pres.type = "sine";
    pres.frequency.value = 196;
    pres.connect(this.presenceGain).connect(this.master);
    pres.start();

    // menace bed — low filtered saw that fades in when hostile creatures are near
    this.menaceGain = this.ctx.createGain();
    this.menaceGain.gain.value = 0;
    const men = this.ctx.createOscillator();
    men.type = "sawtooth";
    men.frequency.value = 58;
    const menFilter = this.ctx.createBiquadFilter();
    menFilter.type = "lowpass";
    menFilter.frequency.value = 180;
    men.connect(menFilter).connect(this.menaceGain).connect(this.master);
    men.start();
  }

  private restartMusicTimer() {
    if (!this.ctx) return;
    if (this.musicTimer) window.clearInterval(this.musicTimer);
    const stepMs = this.bloodMoon ? 115 : 165;
    this.musicTimer = window.setInterval(() => this.musicTick(), stepMs);
  }

  private musicTick() {
    if (!this.ctx || !this.musicGain || !this.enabled) return;
    const step = this.musicStep++ % 32;
    const night = Math.max(0, Math.min(1, this.darknessLevel));
    const root = this.bloodMoon ? 196 : night > 0.55 ? 220 : 247; // G3 / A3 / B3
    const melody = this.bloodMoon
      ? [0, 7, 10, 7, 12, 10, 7, 3, 0, 7, 15, 12, 10, 7, 3, 7]
      : [0, 7, 9, 7, 12, 9, 7, 4, 0, 7, 9, 12, 14, 12, 9, 7];
    const bass = this.bloodMoon
      ? [0, 0, -5, 0, -2, -2, -7, -5]
      : [0, 0, 0, -5, -5, -2, -2, -5];

    // Bass pulse on the beat gives a real loop instead of random beeps.
    if (step % 4 === 0) {
      const bassDeg = bass[Math.floor(step / 4) % bass.length];
      this.note(
        root * Math.pow(2, (bassDeg - 12) / 12),
        this.bloodMoon ? 0.18 : 0.28,
        "triangle",
        this.bloodMoon ? 0.075 : 0.055,
      );
    }

    // Hypnotic arpeggio/melody. Blood Moon plays every other step; normal mode
    // leaves breathing room and accents the same motif more gently.
    const shouldPlayLead = this.bloodMoon ? step % 2 === 0 : step % 4 === 0 || step % 4 === 3;
    if (shouldPlayLead) {
      const deg = melody[step % melody.length] + (step >= 16 ? 12 : 0);
      const freq = root * Math.pow(2, deg / 12);
      this.note(freq, this.bloodMoon ? 0.12 : 0.22, "square", this.bloodMoon ? 0.07 : 0.045);
    }

    // Tiny off-beat tick for ZX-era propulsion. Quiet enough not to be the old
    // annoying plings, but it makes the loop feel intentional.
    if ((this.bloodMoon && step % 2 === 1) || (!this.bloodMoon && step % 8 === 6)) {
      this.note(
        root * Math.pow(2, 19 / 12),
        0.05,
        "square",
        this.bloodMoon ? 0.035 : 0.022,
      );
    }
  }

  private note(freq: number, dur: number, type: OscillatorType, gain: number) {
    if (!this.ctx || !this.musicGain) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = this.bloodMoon ? 1800 : 1350;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(filter).connect(g).connect(this.musicGain);
    osc.start(t);
    osc.stop(t + dur + 0.03);
  }

  // ---- proximity -------------------------------------------------------
  /** Fade the presence/menace beds with the count of nearby friendly/hostile entities. */
  setPresence(friendly: number, hostile: number) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    if (this.presenceGain)
      this.presenceGain.gain.linearRampToValueAtTime(Math.min(0.045, friendly * 0.018), t + 0.6);
    if (this.menaceGain)
      this.menaceGain.gain.linearRampToValueAtTime(Math.min(0.07, hostile * 0.035), t + 0.6);
  }

  /** A new player or friendly NPC came within earshot — a soft, welcoming chime. */
  nearFriendly() {
    this.tone(523, 0.18, "sine", 0.07);
    setTimeout(() => this.tone(784, 0.2, "sine", 0.06), 110);
  }

  /** A hostile creature came within earshot — a low, wary growl. */
  nearHostile() {
    this.noiseBurst(0.22, 90, 0.1);
    this.tone(70, 0.34, "sawtooth", 0.1, 52);
  }

  // ---- one-shot SFX ----------------------------------------------------
  private tone(freq: number, dur: number, type: OscillatorType, gain = 0.18, slideTo?: number) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + dur);
  }

  private noiseBurst(dur: number, freq: number, gain = 0.25) {
    if (!this.ctx || !this.noiseBuffer) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + dur);
  }

  chop() {
    this.noiseBurst(0.12, 320, 0.3);
    this.tone(140, 0.1, "triangle", 0.1);
  }
  mine() {
    this.noiseBurst(0.1, 900, 0.25);
  }
  hit() {
    this.noiseBurst(0.09, 240, 0.3);
    this.tone(90, 0.12, "square", 0.12, 60);
  }
  hurt() {
    this.tone(200, 0.25, "sawtooth", 0.18, 80);
  }
  eat() {
    this.tone(330, 0.12, "sine", 0.12);
    this.tone(440, 0.12, "sine", 0.1);
  }
  craftDone() {
    this.tone(523, 0.12, "triangle", 0.14);
    setTimeout(() => this.tone(784, 0.16, "triangle", 0.14), 90);
  }
  build() {
    this.tone(160, 0.18, "square", 0.16, 110);
    this.noiseBurst(0.12, 500, 0.2);
  }
  levelUp() {
    [523, 659, 784, 1047].forEach((f, i) =>
      setTimeout(() => this.tone(f, 0.18, "triangle", 0.16), i * 80),
    );
  }
  death() {
    this.tone(330, 0.8, "sawtooth", 0.2, 70);
  }
  nightfall() {
    this.tone(196, 1.2, "sine", 0.12, 110);
  }
  dawn() {
    this.tone(392, 1.0, "sine", 0.1, 523);
  }
}

export const audio = new AudioManager();
